// Achievements (~50), daily/weekly quests with DGN + cosmetic rewards, and earned-only cosmetics
// (§8: PFP frames, avatar outfits, phone skins, apartment decor. Nothing is sold).

import { TUNABLES } from "@degen/shared/config/tunables";
import { gas } from "@degen/shared/money";
import type { Rng } from "../rng";
import { dayOf, pushLog, type GameState } from "../state";
import { netWorth } from "../player";
import type { Command } from "../engine";

const Q = TUNABLES.quests;

export type CosmeticSlot = "frame" | "outfit" | "phoneSkin";

export interface CosmeticDef {
  id: string;
  slot: CosmeticSlot | "decor";
  name: string;
  color: string;
}

export const COSMETICS: CosmeticDef[] = [
  { id: "frame:bronze", slot: "frame", name: "Bronze frame", color: "#b45309" },
  { id: "frame:silver", slot: "frame", name: "Silver frame", color: "#cbd5e1" },
  { id: "frame:gold", slot: "frame", name: "Gold frame", color: "#facc15" },
  { id: "frame:diamond", slot: "frame", name: "Diamond frame", color: "#67e8f9" },
  { id: "frame:neon", slot: "frame", name: "Neon frame", color: "#e879f9" },
  { id: "frame:rainbow", slot: "frame", name: "Rainbow frame", color: "#f472b6" },
  { id: "outfit:suit", slot: "outfit", name: "Whale suit", color: "#111827" },
  { id: "outfit:gold", slot: "outfit", name: "Gold drip", color: "#d4a017" },
  { id: "outfit:neon", slot: "outfit", name: "Neon hoodie", color: "#22d3ee" },
  { id: "outfit:camo", slot: "outfit", name: "Trench camo", color: "#4d7c0f" },
  { id: "outfit:builder", slot: "outfit", name: "Builder hi-vis", color: "#f97316" },
  { id: "skin:sunset", slot: "phoneSkin", name: "Sunset wallpaper", color: "#fb923c" },
  { id: "skin:matrix", slot: "phoneSkin", name: "Matrix wallpaper", color: "#22c55e" },
  { id: "skin:gold", slot: "phoneSkin", name: "Gold wallpaper", color: "#facc15" },
  { id: "skin:ocean", slot: "phoneSkin", name: "Ocean wallpaper", color: "#38bdf8" },
  { id: "decor:neon_sign", slot: "decor", name: "WAGMI neon sign", color: "#e879f9" },
  { id: "decor:gold_trophy", slot: "decor", name: "Gold trophy", color: "#facc15" },
  { id: "decor:moon_rug", slot: "decor", name: "Moon rug", color: "#6366f1" },
];

interface Achievement {
  id: string;
  name: string;
  desc: string;
  dgn: number;
  cosmetic?: string;
  check(s: GameState): boolean;
}

const c = (s: GameState, k: string) => s.progress.counters[k] ?? 0;
const nw = (s: GameState) => netWorth(s);

export const ACHIEVEMENTS: Achievement[] = [
  // trading
  { id: "first_trade", name: "First ape", desc: "Make your first trade", dgn: 5, check: (s) => s.player.stats.trades >= 1 },
  { id: "trades_25", name: "Chart addict", desc: "Make 25 trades", dgn: 10, check: (s) => s.player.stats.trades >= 25 },
  { id: "trades_100", name: "Terminally online", desc: "Make 100 trades", dgn: 25, cosmetic: "outfit:camo", check: (s) => s.player.stats.trades >= 100 },
  { id: "trades_500", name: "Market maker energy", desc: "Make 500 trades", dgn: 60, check: (s) => s.player.stats.trades >= 500 },
  { id: "win_1k", name: "Bag secured", desc: "Close a trade W$1k in profit", dgn: 10, check: (s) => s.player.stats.bestTradeUsd >= 1_000 },
  { id: "win_10k", name: "Life changing (small life)", desc: "Close a trade W$10k in profit", dgn: 25, cosmetic: "frame:bronze", check: (s) => s.player.stats.bestTradeUsd >= 10_000 },
  { id: "win_100k", name: "0 to hero", desc: "Close a trade W$100k in profit", dgn: 60, cosmetic: "frame:gold", check: (s) => s.player.stats.bestTradeUsd >= 100_000 },
  { id: "win_1m", name: "Generational", desc: "Close a trade W$1M in profit", dgn: 150, cosmetic: "frame:diamond", check: (s) => s.player.stats.bestTradeUsd >= 1_000_000 },
  { id: "loss_1k", name: "Tuition paid", desc: "Lose W$1k on a single trade", dgn: 5, check: (s) => s.player.stats.worstTradeUsd <= -1_000 },
  // wealth + clout
  { id: "nw_1k", name: "Four figures", desc: "Net worth W$1k", dgn: 5, check: (s) => nw(s) >= 1_000 },
  { id: "nw_10k", name: "Five figures", desc: "Net worth W$10k", dgn: 10, check: (s) => nw(s) >= 10_000 },
  { id: "nw_100k", name: "Six figures", desc: "Net worth W$100k", dgn: 25, cosmetic: "frame:silver", check: (s) => nw(s) >= 100_000 },
  { id: "nw_1m", name: "Millionaire (fictional)", desc: "Net worth W$1M", dgn: 60, cosmetic: "outfit:gold", check: (s) => nw(s) >= 1_000_000 },
  { id: "nw_10m", name: "Whale behaviour", desc: "Net worth W$10M", dgn: 150, cosmetic: "outfit:suit", check: (s) => nw(s) >= 10_000_000 },
  { id: "f_100", name: "Somebody's watching", desc: "100 followers", dgn: 5, check: (s) => s.player.followers >= 100 },
  { id: "f_1k", name: "Micro-influencer", desc: "1,000 followers", dgn: 10, check: (s) => s.player.followers >= 1_000 },
  { id: "f_10k", name: "Blue check vibes", desc: "10,000 followers", dgn: 25, check: (s) => s.player.followers >= 10_000 },
  { id: "f_100k", name: "KOL arc", desc: "100,000 followers", dgn: 60, cosmetic: "frame:neon", check: (s) => s.player.followers >= 100_000 },
  { id: "f_1m", name: "Main character", desc: "1M followers", dgn: 150, cosmetic: "frame:rainbow", check: (s) => s.player.followers >= 1_000_000 },
  { id: "rank_3", name: "Fish", desc: "Reach Fish", dgn: 15, check: (s) => s.player.rank >= 3 },
  { id: "rank_5", name: "Shark", desc: "Reach Shark", dgn: 40, check: (s) => s.player.rank >= 5 },
  { id: "rank_7", name: "Whale", desc: "Reach Whale", dgn: 100, check: (s) => s.player.rank >= 7 },
  { id: "trust_90", name: "Trusted", desc: "Trust 90+", dgn: 20, check: (s) => s.player.trust >= 90 },
  // social
  { id: "posts_10", name: "Poster", desc: "Post 10 times", dgn: 5, check: (s) => s.player.stats.posts >= 10 },
  { id: "posts_100", name: "Posting through it", desc: "Post 100 times", dgn: 20, check: (s) => s.player.stats.posts >= 100 },
  { id: "free_post", name: "In your own words", desc: "Write a freeform post", dgn: 5, check: (s) => c(s, "POST_FREE") >= 1 },
  { id: "texts_20", name: "Group chat menace", desc: "Send 20 texts", dgn: 10, check: (s) => c(s, "SEND_MESSAGE") >= 20 },
  { id: "host_giveaway", name: "Generous", desc: "Host a giveaway", dgn: 10, check: (s) => c(s, "HOST_GIVEAWAY") >= 1 },
  { id: "house_party", name: "House party", desc: "Have 3 guests over at once", dgn: 20, cosmetic: "decor:neon_sign", check: (s) => s.houses.visits.filter((v) => !v.rewarded && s.tick >= v.fromTick && s.tick < v.toTick).length >= 3 },
  // building
  { id: "launch_1", name: "Dev mode", desc: "Launch a coin", dgn: 10, check: (s) => s.player.stats.coinsLaunched >= 1 },
  { id: "launch_honest", name: "Builder", desc: "Launch 3 coins without rugging", dgn: 30, cosmetic: "outfit:builder", check: (s) => s.player.stats.coinsLaunched >= 3 && s.player.stats.rugsPulled === 0 },
  { id: "nft_launch", name: "Gallery opening", desc: "Launch an NFT collection", dgn: 10, check: (s) => s.player.stats.collectionsLaunched >= 1 },
  { id: "hack_ship", name: "Shipped it", desc: "Finish a hackathon", dgn: 10, check: (s) => s.hackathons.skill >= 1 },
  { id: "hack_win", name: "Hackathon champ", desc: "Win a hackathon", dgn: 50, cosmetic: "decor:gold_trophy", check: (s) => s.hackathons.wins >= 1 },
  // farming + luck
  { id: "farm_10", name: "Points farmer", desc: "Do 10 farm tasks", dgn: 5, check: (s) => c(s, "FARM_TASK") >= 10 },
  { id: "farm_100", name: "Professional airdrop hunter", desc: "Do 100 farm tasks", dgn: 25, check: (s) => c(s, "FARM_TASK") >= 100 },
  { id: "presale_5", name: "Ticket collector", desc: "Enter 5 presale draws", dgn: 10, check: (s) => c(s, "ENTER_PRESALE") >= 5 },
  { id: "streak_7", name: "Daily degen", desc: "7-day check-in streak", dgn: 20, cosmetic: "skin:sunset", check: (s) => s.player.gas.streak >= 7 },
  { id: "gas_100", name: "Gas guzzler", desc: "Burn 100 DGN on fees", dgn: 15, check: (s) => s.player.gas.spent >= 100 },
  // perps
  { id: "perp_first", name: "Leverage curious", desc: "Open a perp", dgn: 5, check: (s) => c(s, "OPEN_PERP") >= 1 },
  { id: "perp_win", name: "Perp god", desc: "Close a perp 50+ DGN in profit", dgn: 25, cosmetic: "skin:matrix", check: (s) => s.perps.best >= 50 },
  { id: "perp_rekt", name: "Liquidation speedrun", desc: "Get liquidated", dgn: 5, check: (s) => s.perps.liquidations >= 1 },
  // life
  { id: "first_car", name: "Wheels", desc: "Buy a car or bike", dgn: 10, check: (s) => s.cars.owned.length >= 1 },
  { id: "rides_5", name: "Okada man", desc: "Give 5 people a ride", dgn: 10, check: (s) => (s.cars.rides?.total ?? 0) >= 5 },
  { id: "rides_50", name: "Danfo legend", desc: "Give 50 people a ride", dgn: 30, check: (s) => (s.cars.rides?.total ?? 0) >= 50 },
  { id: "supercar", name: "Lambo moment", desc: "Own a supercar", dgn: 40, check: (s) => s.cars.owned.some((x) => /^(diablo|laserbolt|veyrox|hyperion|w1)$/.test(x.modelId)) },
  { id: "home_owner", name: "Landlord who?", desc: "Buy a house", dgn: 15, check: (s) => s.houses.owned.some((h) => !h.id.startsWith("home_")) },
  { id: "mansion", name: "Whale Mansion", desc: "Own the Whale Mansion", dgn: 80, cosmetic: "decor:moon_rug", check: (s) => s.houses.owned.some((h) => h.id === "h6") },
  { id: "pets_3", name: "Zookeeper", desc: "Have 3 pets", dgn: 10, check: (s) => s.pets.owned.length >= 3 },
  { id: "ifone", name: "Green bubbles no more", desc: "Upgrade to an iFone", dgn: 10, cosmetic: "skin:gold", check: (s) => s.player.phones.some((x) => x.startsWith("ifone")) && s.player.origin === "novice" },
  { id: "grass_10", name: "Outdoorsy", desc: "Touch grass 10 times", dgn: 10, cosmetic: "skin:ocean", check: (s) => c(s, "TOUCH_GRASS") >= 10 },
  { id: "vano", name: "Bot-assisted", desc: "Subscribe to Vano", dgn: 5, check: (s) => c(s, "VANO_SUBSCRIBE") >= 1 },
  { id: "survivor", name: "Survivor", desc: "Make it to day 30", dgn: 40, check: (s) => dayOf(s.tick) >= 30 },
  { id: "quests_10", name: "Quest enjoyer", desc: "Finish 10 quests", dgn: 20, cosmetic: "outfit:neon", check: (s) => s.progress.questsDone >= 10 },
];

// ---------------------------------------------------------------- quests
type QuestKind = "trades" | "posts" | "farm" | "grass" | "checkin" | "texts" | "builds" | "perps" | "coffee" | "launch" | "hack";
const KIND_CMDS: Record<QuestKind, Command["type"][]> = {
  trades: ["BUY_TOKEN", "SELL_TOKEN", "APPLY_FILL"],
  posts: ["POST", "POST_FREE"],
  farm: ["FARM_TASK"],
  grass: ["TOUCH_GRASS"],
  checkin: ["DAILY_CHECKIN"],
  texts: ["SEND_MESSAGE"],
  builds: ["HACK_BUILD"],
  perps: ["OPEN_PERP"],
  coffee: ["COFFEE"],
  launch: ["LAUNCH_COIN", "APPLY_LAUNCH"],
  hack: ["JOIN_HACKATHON"],
};
const DAILY_POOL: [QuestKind, number, string][] = [
  ["trades", 3, "Make 3 trades"], ["posts", 2, "Post twice on Chirp"], ["farm", 2, "Do 2 farm tasks"], ["grass", 1, "Touch grass once"],
  ["checkin", 1, "Check in for DGN"], ["texts", 2, "Text 2 people"], ["coffee", 1, "Grab a coffee"], ["perps", 1, "Open a perp position"],
];
const WEEKLY_POOL: [QuestKind, number, string][] = [
  ["trades", 25, "Make 25 trades"], ["posts", 15, "Post 15 times"], ["farm", 15, "Do 15 farm tasks"], ["launch", 1, "Launch a coin"],
  ["hack", 1, "Enter a hackathon"], ["builds", 4, "Ship 4 hackathon builds"], ["texts", 12, "Send 12 texts"],
];

export interface Quest {
  id: string;
  kind: QuestKind;
  text: string;
  target: number;
  progress: number;
  dgn: number;
  cosmetic?: string;
  done: boolean;
}

export interface ProgressState {
  achievements: Record<string, number>; // id -> tick earned
  counters: Record<string, number>; // lifetime command counts
  daily: Quest[];
  weekly: Quest[];
  dailyDay: number;
  weeklyWeek: number;
  questsDone: number;
  cosmetics: string[];
  equipped: Partial<Record<CosmeticSlot, string>>;
}

export function createProgress(): ProgressState {
  return { achievements: {}, counters: {}, daily: [], weekly: [], dailyDay: 0, weeklyWeek: -1, questsDone: 0, cosmetics: [], equipped: {} };
}

function grantCosmetic(state: GameState, id: string) {
  if (!state.progress.cosmetics.includes(id)) state.progress.cosmetics.push(id);
}

function pick(rng: Rng, pool: [QuestKind, number, string][], n: number, dgn: [number, number], prefix: string, weekly: boolean): Quest[] {
  const left = [...pool];
  const out: Quest[] = [];
  for (let i = 0; i < n && left.length; i++) {
    const [kind, target, text] = left.splice(rng.int(0, left.length - 1), 1)[0];
    const cos = weekly && rng.chance(0.35) ? rng.pick(COSMETICS.filter((x) => x.slot === "phoneSkin" || x.slot === "outfit")).id : undefined;
    out.push({ id: `${prefix}${i}`, kind, text, target, progress: 0, dgn: Math.round(rng.range(dgn[0], dgn[1])), cosmetic: cos, done: false });
  }
  return out;
}

/** New day: fresh dailies; every 7 days: fresh weeklies. */
export function questsNewDay(state: GameState, rng: Rng, day: number) {
  const pr = state.progress;
  if (pr.dailyDay !== day) {
    pr.daily = pick(rng, DAILY_POOL, Q.daily, Q.dailyDgn, `d${day}-`, false);
    pr.dailyDay = day;
  }
  const week = Math.floor((day - 1) / 7);
  if (pr.weeklyWeek !== week) {
    pr.weekly = pick(rng, WEEKLY_POOL, Q.weekly, Q.weeklyDgn, `w${week}-`, true);
    pr.weeklyWeek = week;
  }
}

/** After every successful command: counters, quest progress, achievements. */
export function progressOnCommand(state: GameState, cmd: Command) {
  const pr = state.progress;
  pr.counters[cmd.type] = (pr.counters[cmd.type] ?? 0) + 1;
  for (const q of [...pr.daily, ...pr.weekly]) {
    if (q.done || !KIND_CMDS[q.kind].includes(cmd.type)) continue;
    q.progress++;
    if (q.progress >= q.target) {
      q.done = true;
      pr.questsDone++;
      state.player.dgn += q.dgn;
      if (q.cosmetic) grantCosmetic(state, q.cosmetic);
      pushLog(state, "quest_done", `✅ Quest done: ${q.text}. +${gas(q.dgn)}${q.cosmetic ? ` + ${COSMETICS.find((x) => x.id === q.cosmetic)?.name}` : ""}`);
    }
  }
  checkAchievements(state);
}

export function checkAchievements(state: GameState) {
  const pr = state.progress;
  for (const a of ACHIEVEMENTS) {
    if (pr.achievements[a.id] !== undefined || !a.check(state)) continue;
    pr.achievements[a.id] = state.tick;
    state.player.dgn += a.dgn;
    if (a.cosmetic) grantCosmetic(state, a.cosmetic);
    pushLog(state, "achievement", `🏅 ${a.name}: ${a.desc}. +${gas(a.dgn)}${a.cosmetic ? ` + ${COSMETICS.find((x) => x.id === a.cosmetic)?.name}` : ""}`);
  }
}

type Result = { ok: true } | { ok: false; error: string };

/** Equip an earned cosmetic (or null to take it off). Season rewards from the server count too. */
export function equip(state: GameState, slot: CosmeticSlot, item: string | null, serverCosmetics: string[] = []): Result {
  if (item === null) {
    delete state.progress.equipped[slot];
    return { ok: true };
  }
  const owned = state.progress.cosmetics.includes(item) || serverCosmetics.includes(item);
  if (!owned) return { ok: false, error: "Earn it first. Cosmetics can't be bought." };
  if (!item.startsWith(slot === "phoneSkin" ? "skin:" : `${slot}:`)) return { ok: false, error: "Wrong slot." };
  state.progress.equipped[slot] = item;
  return { ok: true };
}
